<template>
	<glass-card
		class="container my-5 p-5 signup"
		borderColor="#4ade80aa"
		background="#0b1220aa"
		blur="12px">
		<h2>Create your account</h2>
		<p class="mt-2">
			A few seconds to set up — then it’s just snap, log, done.
		</p>
		<div class="mt-3">
			<p>
				Need to sign in?
				<router-link to="/signin">Sign in instead</router-link>
			</p>
		</div>
		<o-auth />
		<hr />
		<form @submit.prevent="handleSubmit">
			<!-- <div class="mb-3">
				<label for="inviteCode" class="form-label">Invitation Code</label>
				<input
					type="text"
					class="form-control"
					id="inviteCode"
					v-model.trim="form.inviteCode"
					required />
			</div>
			<div class="mb-3" v-if="form.inviteCode.length < 36">
				<div
					class="card border-info bg-light shadow-sm"
					style="max-width: 100%">
					<div class="card-body d-flex align-items-center">
						<span class="me-3" style="font-size: 2rem">🎟️</span>
						<div>
							<strong>No invite code?</strong><br />
							<span class="text-secondary"
								>We're still in early access! Hop on the waitlist and be among
								the first to join GrubVision.</span
							><br />
							<a
								href="https://alpha.grubvision.com"
								target="_blank"
								rel="noopener"
								class="btn btn-info btn-sm mt-2 fw-bold"
								>Join the Waitlist 🚀</a
							>
						</div>
					</div>
				</div>
			</div> -->
			<div class="mb-3">
				<label for="name" class="form-label">Name</label>
				<input
					type="text"
					class="form-control"
					id="name"
					v-model.trim="form.name"
					required />
			</div>
			<div class="mb-3">
				<label for="email" class="form-label">Email address</label>
				<input
					type="email"
					class="form-control"
					id="email"
					v-model.trim="form.email"
					required />
			</div>
			<!-- Password field -->
			<PasswordWithRequirements
				v-model="form.password"
				label="Password"
				transparent="true"
				@valid="passwordValid = $event" />
			<!-- Confirm Password field -->
			<PasswordInput
				v-model="form.confirmPassword"
				label="Confirm Password"
				transparent="true" />
			<div class="mb-3 form-check">
				<input
					type="checkbox"
					class="form-check-input"
					id="ageCheck"
					v-model="form.isThirteenOrOlder" />
				<label class="form-check-label" for="ageCheck"
					>I am 13 years old or above</label
				>
			</div>
			<div class="mb-3 form-check">
				<input
					type="checkbox"
					class="form-check-input"
					id="termsCheck"
					v-model="form.agreedToTerms" />
				<label class="form-check-label" for="termsCheck"
					>I agree to the Terms of Use and Privacy Policy</label
				>
			</div>
			<turnstile siteKey="0x4AAAAAAA0HUE46ATCN9t1U" @token="token" />
			<button type="submit" class="btn mt-3 bprimary">Submit</button>
		</form>
		<div class="mt-5 fs-6">
			By continuing, you agree to the Terms and Privacy Policy.
		</div>
	</glass-card>
</template>

<script setup>
	import { ref, onMounted } from "vue"
	import { useTokenStore } from "@/stores/token"
	import { useRouter } from "vue-router"
	import Swal from "sweetalert2"
	import Turnstile from "@/components/Turnstile.vue"
	import PasswordInput from "@/components/PasswordInput.vue"
	import PasswordWithRequirements from "@/components/PasswordWithRequirements.vue"
	import OAuth from "@/components/OAuth.vue"
	import GlassCard from "@/components/GlassCard.vue"

	const tokenStore = useTokenStore()
	const router = useRouter()

	const form = ref({
		name: "",
		email: "",
		password: "",
		confirmPassword: "",
		isThirteenOrOlder: false,
		agreedToTerms: false,
		inviteCode: "",
		token: "",
	})

	const passwordValid = ref(false)

	function token(token) {
		form.value.token = token
	}

	const validateForm = () => {
		if (!passwordValid.value) {
			Swal.fire({
				icon: "error",
				title: "Password does not meet requirements.",
			})
			window.turnstile.reset()
			return false
		}
		if (form.value.password !== form.value.confirmPassword) {
			Swal.fire({ icon: "error", title: "Passwords do not match." })
			window.turnstile.reset()
			return false
		}
		if (!form.value.isThirteenOrOlder || !form.value.agreedToTerms) {
			Swal.fire({
				icon: "error",
				title: "You must be 13 or older and agree to the terms.",
			})
			window.turnstile.reset()
			return false
		}
		if (!form.value.name) {
			Swal.fire({
				icon: "error",
				title: "You must have the name field filled out.",
			})
			window.turnstile.reset()
			return false
		}
		return true
	}

	const handleSubmit = async () => {
		if (!validateForm()) return

		try {
			const response = await fetch("/api/signup", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(form.value),
			})

			// if (!response.ok) throw new Error("Network response was not ok.")

			const data = await response.json()
			console.log(data)
			if (data.msg || data.error) {
				Swal.fire({ icon: "error", title: data.msg ?? data.error })
				window.turnstile.reset()
				return
			}

			// if (data.token) {
			await tokenStore.setTokens(data.access_token, data.refresh_token)
			router.push({ name: "dashboard" })
			// }
		} catch (error) {
			console.error("Error:", error)
			Swal.fire({ icon: "error", title: error.message })
			window.turnstile.reset()
		}
	}

	onMounted(() => {
		const urlParams = new URLSearchParams(window.location.search)
		form.value.inviteCode = urlParams.get("invite") || ""
	})
</script>

<style scoped>
	/* Add your styles here */

	.signup {
		max-width: 500px;
		margin: auto;
	}

	/* Transparent inputs for this view */
	.form-control {
		background-color: transparent !important;
		color: #ffffff !important;
		border-color: rgba(255, 255, 255, 0.25);
	}
	.form-control:focus {
		background-color: transparent !important;
		color: #ffffff !important;
		box-shadow: 0 0 0 0.2rem rgba(34, 197, 94, 0.25);
		border-color: #22c55e;
	}
	.form-floating > label {
		background-color: transparent !important;
		color: #cbd5e1;
	}
	.form-control::placeholder {
		color: #94a3b8;
		opacity: 0.8;
	}

	/* Button style */

	.bprimary {
		width: 100%;
		border: none;
		border-radius: 14px;
		padding: 12px 16px;
		font-weight: 700;
		font-size: 1.05rem;
		background: linear-gradient(180deg, #34d399, #22c55e);
		color: #062312;
		cursor: pointer;
		box-shadow: 0 10px 24px #22c55e55, inset 0 1px 0 #fff8;
		transition: transform 0.06s ease, box-shadow 0.2s ease, filter 0.2s ease;
	}
	.bprimary:hover {
		transform: translateY(-1px);
		filter: brightness(1.02);
	}
	.bprimary:active {
		transform: translateY(0);
		box-shadow: 0 6px 16px #22c55e55;
	}
</style>
